'use client';

import { action, autorun, makeObservable, observable } from 'mobx';
import samplesSelectorModel from '../SamplesSelector/SamplesSelectorModel';
import samplesModel, { popupCoords, SAMPLES_MODE } from '../SamplesModel';
import { apiSwagger } from '../../../shared/api';
import { createI18nObj, rightLanguage, setValueToi18nObj } from '@/sharedTypes/helpers';
import { triggerEvent } from '@/sharedTypes/pureHelpers';
import { SamplesEditorModel } from '../SamplesEditor/SamplesEditorModel';
import jsSHA from 'jssha';
import { ManagerEntity } from '@/shared/globalFunctions';
import { safeWindow } from '@/sharedTypes/browserUtils';
import toast from '@/components/ui/custom-toast';
import { t } from '@/shared/i18n';
import { v4 } from 'uuid';
import type { Author, Category } from '@/sharedTypes/Api';
import { I18nObject } from '@/sharedTypes/helpers';
import etaLoader from '@/shared/EtaLoader';

interface metadataInterface {
  title: Record<string, string>;
  author: string; // ObjectId string, not Author object
  midiLoaded: boolean;
  midiWrongNotesCount: number;
}

export class SamplesUploaderModel {
  uploadCategory: Category = {
    _id: v4(),
    en: 'sample category',
    ru: 'пример категории',
  };
  createdCategories: Category[] = [];
  get allCategories(): Category[] {
    return samplesSelectorModel.categories.concat(this.createdCategories);
  }

  loadedTitle: I18nObject = { 
    en: 'Sample title',
    ru: 'Пример заголовка'
  };

  loadedAuthor: Author = {
    _id: v4(),
    en: 'sample author',
    ru: 'пример автора',
  };
  loadedMusicxml: File | null = null;
  loadedMidi: File | null = null;

  reset() {
    this.loadedTitle = {
      en: "",
      ru: ""
    };
    this.loadedAuthor = {
      _id: v4(),
      ...createI18nObj(),
    };
    this.loadedMusicxml = null;
    this.loadedMidi = null;
    this.createdCategories = [];
  }

  constructor() {
    makeObservable(this, {
      uploadCategory: observable,
      loadedTitle: observable,
      loadedAuthor: observable,
      loadedMusicxml: observable,
      loadedMidi: observable,
      loadNewMusicxmlFile: action,
      uploadLoadedMusicxml: action,
    });
    autorun(() => {
      if (
        samplesSelectorModel &&
        samplesSelectorModel.samples &&
        samplesSelectorModel.samples.length
      ) {
        const samplesSelectorCategories = samplesSelectorModel.categories;
        if (samplesSelectorCategories.length > 0) {
          this.uploadCategory = samplesSelectorCategories[0];
        }
      }
    });
  }

  loadNewMusicxmlFile(file?: File) {
    if (file) {
      this.loadedMusicxml = file;
      return Promise.resolve();
    }
    return Promise.resolve();
  }

  loadNewMidiFile(file?: File) {
    if (file) {
      this.loadedMidi = file;
      return Promise.resolve();
    }
    return Promise.resolve();
  }

  readFileAsArrayBuffer(file: any): Promise<ArrayBuffer> {
    return new Promise((resolve) => {
      const fr = new FileReader();
      fr.onload = () => {
        resolve(fr.result as ArrayBuffer);
      };
      fr.readAsArrayBuffer(file);
    });
  }

  getMusicXml(fileBuffer: ArrayBuffer): Promise<string> {
    return Promise.resolve(new TextDecoder().decode(fileBuffer));
  }

  async uploadLoadedMusicxml() {
    try {
      if (!ManagerEntity?.core) {
        console.error('Ошибка: Основные функции недоступны');
        return Promise.reject('Core functionality not available');
      }

      if (!this.loadedMusicxml || !this.loadedAuthor || !this.loadedTitle) {
        toast({
          title: t('error'),
          description: t('samples.uploader.missing_required_fields'),
          type: 'error',
        });
        return Promise.reject('Missing required fields');
      }

      // safeWindow.spinner.show('Uploading musicxml...', popupCoords);
      if(typeof window !== 'undefined' && etaLoader) {
        etaLoader.setContext('uploadXML');
        etaLoader.setStage('uploadXML_start', 0);
      }

      const fileBuffer = await this.readFileAsArrayBuffer(this.loadedMusicxml);
      const musicXml = await this.getMusicXml(fileBuffer);
      if (!musicXml || !ManagerEntity?.core) {
        throw new Error('Invalid musicXml or core not available');
      }
      if(typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('uploadXML_start', 0.2);
      }      
    const createdAuthorRes = await apiSwagger.authors.authorsCreate({
      en: this.loadedAuthor.en,
      ru: this.loadedAuthor.ru,
    });
    const createdAuthor = await createdAuthorRes.json();
    const author: Author = createdAuthor.data;
    if(typeof window !== 'undefined' && etaLoader) {
      etaLoader.setStage('uploadXML_start', 0.5);
    }
  const createdCategoryRes = await apiSwagger.categories.categoriesCreate({
    en: this.uploadCategory.en,
    ru: this.uploadCategory.ru,
  });
  const createdCategory = await createdCategoryRes.json();
  const category: Category = createdCategory.data;
  if(typeof window !== 'undefined' && etaLoader) {
    etaLoader.setStage('uploadXML_start', 0.7);
  }
// - Then modify the sample creation request to use these IDs
    const title = { ...this.loadedTitle };
    
      const metadata: metadataInterface = {
        title,
        author: author._id, // ObjectId
        midiLoaded: false,
        midiWrongNotesCount: 0,
      };
      if(typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('uploadXML_start', 0.8);
      }
      const cache = ManagerEntity.core.computeFreshTimemap(musicXml, false);
      const sample = await this.createSample(
        category._id,
        this.loadedMusicxml!.name,
        musicXml,
        metadata,
        cache,
        popupCoords
      );

      if (!sample) {
        throw new Error('Failed to create sample');
      }
      if(typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('uploadXML_start', 1);
        etaLoader.setStage('uploadXML_done', 0);
      }
      if (this.loadedMidi) {
        const midiBuffer = await this.readFileAsArrayBuffer(this.loadedMidi);
        const cache = await SamplesEditorModel.computeCacheWithDecryptedMusicxmlAndMidi(
          musicXml,
          midiBuffer,
          sample
        );
        if(typeof window !== 'undefined' && etaLoader) {
          etaLoader.setStage('uploadXML_done', 0.2);
        }
        await apiSwagger.samples.samplesUpdate({ sample: sample });
        const cacheReplacedWithMidi = await SamplesUploaderModel.replaceCache(
          sample,
          cache
        );
        if(typeof window !== 'undefined' && etaLoader) {
          etaLoader.setStage('uploadXML_done', 0.5);
        }
        if (cacheReplacedWithMidi) {
          triggerEvent('Cache with midi update');
        }
        if(typeof window !== 'undefined' && etaLoader) {
          etaLoader.setStage('uploadXML_done', 0.6);
        }
        const res = await SamplesEditorModel.updateMidi(
          this.loadedMidi!,
          sample
        );
        
        if (!res) {
          throw new Error('Failed to update midi');
        }
      }
      if(typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('uploadXML_done', 0.8);
      }
      triggerEvent('Sample uploaded');
    } catch (error: any) {
      // safeWindow.spinner.hide();
      if(typeof window !== 'undefined' && etaLoader) {
        etaLoader.setStage('uploadXML_done', 1);
      }
      console.error('Error uploading musicxml:', error);
      toast({
        title: t('error'),
        description: error.message || t('samples.uploader.upload_failed'),
        type: 'error',
      });
    }

    await samplesSelectorModel.refreshSamples();
    // safeWindow.spinner.hide();
    if(typeof window !== 'undefined' && etaLoader) {
      etaLoader.setStage('uploadXML_done', 1);
    }
    samplesModel.currentMode = SAMPLES_MODE.SELECT;
    this.reset();
  }

  createSample(
    category: string,
    musicxmlFilename: string,
    musicxml: string,
    metadata: any['metadata'],
    cache: any,
    popupCoords?: any | undefined
  ): Promise<any | undefined> {
    const shaObj = new jsSHA('SHA-512', 'TEXT', { encoding: 'UTF8' });
    shaObj.update(cache);
    const hash = shaObj.getHash('HEX');

    const requestBody = {
      musicxml,
      musicxmlFilename,
      hash,
      cache,
      metadata,
      category,
    };
    console.log("Request body:", requestBody);
    return apiSwagger.samples
      .samplesCreate(requestBody as any)
      .then((res) => {
        console.log('Response:', res);
        if (res.ok) {
          return res.json();
        }
        throw new Error(res.error.error);
      })
      .then((resParsed) => resParsed.data)
      .catch((error) => {
        console.error('Ошибка: ' + error.message);
        return undefined;
      });
  }

  static replaceCache(
    sample: Record<string, any>,
    cache: object
  ): Promise<boolean> {
    const cacheStr = JSON.stringify(cache);
    const requestBody = {
      sampleId: sample._id,
      cache: cacheStr,
      cacheLength: cacheStr.length.toString(),
    };

    return apiSwagger.samples
      .cacheUpdate(requestBody)
      .then((res) => {
        if (res.ok) {
          return res.json().then((data) => {
            console.log('Успешно: Загружено', data);
            return true;
          });
        }
        console.error('Ошибка: Произошла ошибка при обновлении кэша');
        return false;
      })
      .catch(() => {
        console.error('Ошибка: Произошла ошибка при обновлении кэша');
        return false;
      });
  }
}

const samplesUploaderModel = new SamplesUploaderModel();
export default samplesUploaderModel;
